import { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  Button,
} from "@mui/material";
import AdminFamilySelect from "components/creation/AdminFamilySelect";
import GridPage from "components/layout/GridPage";
import useIsMobile from "components/hooks/useIsMobile";
import { CurrentUserContext } from "components/context/RequireAuth";
import { isUserAdmin } from "common/userUtils";
import FamilySelect from "components/creation/FamilySelect";
import { useListAdvisorInvoices } from "services/billing";
import Loading from "components/common/Loading";
import { ListAdvisorInvoicesResponse_AdvisorInvoice } from "protogen/billing_service_pb";
import { useSearchParams } from "react-router-dom";
import LinkRouter from "components/navigation/LinkRouter";

const formatCurrency = (cents: number): string => {
  return `$${(cents / 100).toFixed(2)}`;
};

const formatDateRange = (
  startDate: string,
  endDate: string,
  year: number,
): string => {
  return `${startDate} - ${endDate}, ${year}`;
};

const normalizePayoutState = (state: string): "paid" | "pending" | "hold" => {
  if (state === "paid") return "paid";
  if (state === "deferred" || state === "archived") return "hold";
  return "pending";
};

const PayoutStatusChip = ({ state }: { state: string }) => {
  const normalized = normalizePayoutState(state);

  const config = {
    paid: {
      label: "Paid",
      tooltip:
        "Please allow up to 5 business days for the transfer to arrive in your account.",
      color: "success",
    },
    pending: {
      label: "Pending",
      tooltip:
        "Faye processes payment to the advisor 5 business days after the completion of the service month.",
      color: "warning",
    },
    hold: {
      label: "Hold",
      tooltip: "This payment has been placed on hold.",
      color: "info",
    },
  }[normalized];

  return (
    <Tooltip title={config.tooltip} arrow>
      <Chip label={config.label} size="small" color={config.color as any} />
    </Tooltip>
  );
};

const formatTransactionType = (type: string, serviceHours: number): string => {
  if (!type) return "-";
  const formatted = type
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  if (!serviceHours) return formatted;
  if (type === "extra-hours") {
    return `${formatted} (${serviceHours})`;
  }
  return `${formatted} (${serviceHours} hours)`;
};

const formatPaidDate = (paidDateSec: number): string => {
  if (!paidDateSec) return "";
  const date = new Date(paidDateSec * 1000);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const escapeCSVValue = (value: string): string => {
  if (
    value.includes(",") ||
    value.includes('"') ||
    value.includes("\n") ||
    value.includes("\r")
  ) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

const getInvoiceRows = (
  invoices: ListAdvisorInvoicesResponse_AdvisorInvoice[],
): string[][] => {
  const headers = [
    "Service Period",
    "Family",
    "Type",
    "Hours",
    "Amount",
    "Advisor Payment",
    "Status",
    "Paid Date",
  ];

  const rows = invoices.map((invoice) => {
    const year = new Date(Number(invoice.endDateSec) * 1000).getFullYear();
    return [
      `${invoice.startDate} - ${invoice.endDate}, ${year}`,
      invoice.familyName,
      invoice.transactionType
        .split("-")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" "),
      invoice.serviceHours.toString(),
      formatCurrency(invoice.amountCents),
      invoice.payoutAmountCents
        ? formatCurrency(invoice.payoutAmountCents)
        : "-",
      normalizePayoutState(invoice.payoutState),
      invoice.paidDateSec > 0
        ? formatPaidDate(Number(invoice.paidDateSec))
        : "",
    ];
  });

  return [headers, ...rows];
};

const convertInvoicesToCSV = (
  invoices: ListAdvisorInvoicesResponse_AdvisorInvoice[],
): string => {
  const rows = getInvoiceRows(invoices);
  return rows.map((row) => row.map(escapeCSVValue).join(",")).join("\r\n");
};

const convertInvoicesToTSV = (
  invoices: ListAdvisorInvoicesResponse_AdvisorInvoice[],
): string => {
  const rows = getInvoiceRows(invoices);
  return rows.map((row) => row.join("\t")).join("\n");
};

const downloadCSV = (
  invoices: ListAdvisorInvoicesResponse_AdvisorInvoice[],
): void => {
  const csvData = convertInvoicesToCSV(invoices);
  const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const today = new Date();
  const filename = `FamilyInvoices.${today.getFullYear()}${String(
    today.getMonth() + 1,
  ).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}.csv`;
  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const InvoiceTable = ({
  invoices,
  isMobile,
}: {
  invoices: ListAdvisorInvoicesResponse_AdvisorInvoice[];
  isMobile: boolean;
}) => {
  if (invoices.length === 0) {
    return (
      <Typography variant="body" sx={{ padding: "20px" }}>
        No invoices found.
      </Typography>
    );
  }

  const sortedInvoices = [...invoices].sort(
    (a, b) => Number(b.endDateSec) - Number(a.endDateSec),
  );

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        {sortedInvoices.map((invoice, idx) => (
          <Box
            key={idx}
            sx={{
              border: "1px solid #D4D4D4",
              borderRadius: "8px",
              padding: "16px",
            }}
          >
            <LinkRouter
              to={`/families/${encodeURIComponent(invoice.familyRef)}`}
              sx={{ color: "#198282", textDecoration: "none" }}
            >
              <Typography variant="bodyHeavy" sx={{ color: "inherit" }}>
                {invoice.familyName}
              </Typography>
            </LinkRouter>
            <Typography variant="body">
              {formatDateRange(
                invoice.startDate,
                invoice.endDate,
                new Date(Number(invoice.endDateSec) * 1000).getFullYear(),
              )}
            </Typography>
            <Typography variant="bodySmall" color="text.secondary">
              {formatCurrency(invoice.amountCents)} &middot;{" "}
              {formatTransactionType(
                invoice.transactionType,
                invoice.serviceHours,
              )}
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginTop: "12px",
              }}
            >
              <Typography variant="bodySmall" color="text.secondary">
                Advisor Payment:
              </Typography>
              <Typography variant="body">
                {invoice.payoutAmountCents
                  ? formatCurrency(invoice.payoutAmountCents)
                  : "-"}
              </Typography>

              <Typography variant="bodySmall" color="text.secondary">
                Paid:
              </Typography>
              {invoice.paidDateSec > 0 ? (
                <Tooltip
                  title="Please allow up to 5 business days for the transfer to arrive in your account."
                  arrow
                >
                  <Typography variant="body" sx={{ cursor: "help" }}>
                    {formatPaidDate(Number(invoice.paidDateSec))}
                  </Typography>
                </Tooltip>
              ) : (
                <PayoutStatusChip state={invoice.payoutState} />
              )}
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} sx={{ boxShadow: "none" }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <Typography variant="bodyHeavy">Service</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="bodyHeavy">Family</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="bodyHeavy">Advisor Payment</Typography>
            </TableCell>
            <TableCell>
              <Typography variant="bodyHeavy">Paid</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedInvoices.map((invoice, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <Box>
                  <Typography variant="bodyHeavy">
                    {formatDateRange(
                      invoice.startDate,
                      invoice.endDate,
                      new Date(Number(invoice.endDateSec) * 1000).getFullYear(),
                    )}
                  </Typography>
                  <Typography variant="bodySmall" color="text.secondary">
                    {formatTransactionType(
                      invoice.transactionType,
                      invoice.serviceHours,
                    )}
                    {" • "}
                    {formatCurrency(invoice.amountCents)}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell>
                <LinkRouter
                  to={`/families/${encodeURIComponent(invoice.familyRef)}`}
                  sx={{ color: "#198282", textDecoration: "none" }}
                >
                  <Typography variant="body" sx={{ color: "inherit" }}>
                    {invoice.familyName}
                  </Typography>
                </LinkRouter>
              </TableCell>
              <TableCell>
                <Typography variant="body" sx={{ color: "inherit" }}>
                  {invoice.payoutAmountCents
                    ? formatCurrency(invoice.payoutAmountCents)
                    : "-"}
                </Typography>
              </TableCell>
              <TableCell>
                {invoice.paidDateSec > 0 ? (
                  <Tooltip
                    title="Please allow up to 5 business days for the transfer to arrive in your account."
                    arrow
                  >
                    <Typography variant="body" sx={{ cursor: "help" }}>
                      {formatPaidDate(Number(invoice.paidDateSec))}
                    </Typography>
                  </Tooltip>
                ) : (
                  <PayoutStatusChip state={invoice.payoutState} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default () => {
  const isMobile = useIsMobile();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedFamily, setSelectedFamily] = useState<string | null>(
    searchParams.get("family") || null,
  );
  const { request, data, loading } = useListAdvisorInvoices();

  useEffect(() => {
    request({ familyRef: selectedFamily || "" });
    if (selectedFamily) {
      setSearchParams({ family: selectedFamily }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  }, [selectedFamily]);

  return (
    <GridPage
      sx={{
        margin: isMobile ? "" : "auto",
        maxWidth: "1000px",
        padding: isMobile ? "20px" : "64px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography variant="title" id="tableTitle">
            Family Invoices
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          {isAdmin ? (
            <AdminFamilySelect
              selectedFamily={selectedFamily}
              onChange={setSelectedFamily}
              title="Filter by Family"
              disabled={loading}
            />
          ) : (
            <FamilySelect
              selectedFamily={selectedFamily}
              onChange={setSelectedFamily}
              disabled={loading}
            />
          )}

          {loading && <Loading />}

          {!loading && data?.invoices && data.invoices.length > 0 && (
            <>
              {data.invoices.some(
                (invoice) =>
                  new Date(Number(invoice.endDateSec) * 1000) <
                  new Date("2025-02-01"),
              ) && (
                <Alert severity="warning">
                  Some invoices displayed are from before our updated billing
                  system launched in January 2025. Details for these older
                  invoices may not be accurate. Please contact{" "}
                  <a href="mailto:billing@findfaye.com">billing@findfaye.com</a>{" "}
                  with any questions.
                </Alert>
              )}
              <Box
                sx={{
                  border: "1px solid #D4D4D4",
                  borderRadius: "12px",
                  overflow: "hidden",
                }}
              >
                <InvoiceTable invoices={data.invoices} isMobile={isMobile} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  gap: "16px",
                }}
              >
                <Button onClick={() => downloadCSV(data.invoices)}>
                  Download
                </Button>
                <Button
                  variant="outlined"
                  onClick={() =>
                    navigator.clipboard.writeText(
                      convertInvoicesToTSV(data.invoices),
                    )
                  }
                >
                  Copy to Clipboard
                </Button>
              </Box>
            </>
          )}

          {!loading && (!data?.invoices || data.invoices.length === 0) && (
            <Alert severity="info">No invoices found.</Alert>
          )}
        </Box>
      </Box>
    </GridPage>
  );
};
